import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Sparkles, Download, Loader2, FileText, Globe } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { NOTES_LANGUAGES, detectDefaultNotesLanguage, persistNotesLanguage } from "../data/notes_languages";

/**
 * Inline notes recorder for the meeting header. Uses the browser's Web Speech
 * API for free in-browser transcription, then ships the transcript to
 * /platform/meeting-notes/summarise (Gemini) on stop. No audio leaves the
 * browser; only typed text is sent.
 *
 * Web Speech API is supported in Chrome / Edge / Safari (desktop + iOS 14.5+).
 * Firefox falls back to a friendly "not supported" tooltip.
 *
 * Language: the API cannot auto-detect, so we ship a curated picker (defaults
 * to the user's browser language, falling back to en-GB). Saved per-browser.
 */

function getRecognition(lang) {
  if (typeof window === "undefined") return null;
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const r = new SR();
  r.continuous = true;
  r.interimResults = true;
  r.lang = lang || "en-GB";
  return r;
}

export default function MeetingNotesInline({ meetingTitle, meetingCode }) {
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [lang, setLang] = useState(() => detectDefaultNotesLanguage());
  const recRef = useRef(null);
  const restartRef = useRef(true);

  useEffect(() => {
    const r = getRecognition(lang);
    if (!r) { setSupported(false); return; }
    r.onresult = (e) => {
      let finalText = "";
      let interimText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) finalText += res[0].transcript + " ";
        else interimText += res[0].transcript;
      }
      if (finalText) setTranscript((t) => (t + finalText).replace(/\s+/g, " ").trim() + " ");
      setInterim(interimText);
    };
    r.onend = () => {
      if (restartRef.current) {
        try { r.start(); } catch (e) { /* ignore */ }
      }
    };
    r.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast.error("Microphone permission denied. Allow it in your browser settings.");
        setRecording(false);
        restartRef.current = false;
      }
    };
    recRef.current = r;
    return () => {
      restartRef.current = false;
      try { r.stop(); } catch (e) { /* ignore */ }
    };
  }, [lang]);

  // Persist the chosen language. If recording is in progress, restart the
  // recognizer with the new language so the user immediately hears the change.
  const changeLang = (code) => {
    setLang(code);
    persistNotesLanguage(code);
    const wasRecording = recording;
    if (wasRecording) {
      restartRef.current = false;
      try { recRef.current?.stop(); } catch (e) { /* ignore */ }
    }
    // The useEffect above will rebuild recRef with the new lang. Restart
    // recording shortly after if it was active.
    if (wasRecording) {
      setTimeout(() => {
        restartRef.current = true;
        try { recRef.current?.start(); } catch (e) { /* ignore */ }
      }, 300);
    }
  };

  const start = () => {
    if (!recRef.current) return;
    restartRef.current = true;
    try {
      recRef.current.start();
      setRecording(true);
      setShowPanel(true);
      toast.success("Notes recording started, speak naturally");
    } catch (e) { /* already started */ }
  };

  const stop = () => {
    restartRef.current = false;
    try { recRef.current?.stop(); } catch (e) { /* ignore */ }
    setRecording(false);
    setInterim("");
  };

  const summariseAndSave = async () => {
    if (!transcript.trim() || transcript.trim().length < 20) {
      toast.error("Need at least a few sentences first.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/platform/meeting-notes/summarise", {
        transcript: transcript.trim(),
        title: meetingTitle ? `${meetingTitle}${meetingCode ? ` (${meetingCode})` : ""}` : null,
      });
      if (res.data.saved_id) setSavedId(res.data.saved_id);
      toast.success("Summary generated and saved to your notes");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate summary.");
    } finally {
      setBusy(false);
    }
  };

  const download = (format) => {
    if (!transcript.trim()) {
      toast.error("Nothing to export yet, start recording first.");
      return;
    }
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const safeTitle = (meetingTitle || "meeting").replace(/[^a-z0-9-_]+/gi, "_").slice(0, 60);
    const filename = `notes-${safeTitle}-${ts}.${format === "md" ? "md" : "txt"}`;
    const header = format === "md"
      ? `# ${meetingTitle || "Sangha meeting"}\n\n_Recorded ${new Date().toLocaleString("en-GB")}${meetingCode ? ` · Code ${meetingCode}` : ""}_\n\n## Transcript\n\n`
      : `${meetingTitle || "Sangha meeting"}\nRecorded ${new Date().toLocaleString("en-GB")}${meetingCode ? ` (Code ${meetingCode})` : ""}\n\n`;
    const blob = new Blob([header + transcript.trim() + "\n"], {
      type: format === "md" ? "text/markdown" : "text/plain",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  if (!supported) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white/5 border border-white/10 text-pearl/50"
        title="Web Speech API not available in this browser. Use Chrome, Edge or Safari."
      >
        <FileText size={13} /> Notes (n/a)
      </span>
    );
  }

  return (
    <div className="relative">
      <div className="inline-flex items-center gap-1.5">
        {!recording ? (
          <button
            onClick={start}
            data-testid="meeting-notes-start"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white/5 border border-white/15 text-pearl/80 hover:border-saffron hover:text-saffron"
            title={`Start AI Notes — transcribe locally in ${NOTES_LANGUAGES.find((l) => l.code === lang)?.label || lang}, then summarise. Click ${NOTES_LANGUAGES.find((l) => l.code === lang)?.label?.split(" ")[0] || "language"} button (in panel) to change.`}
          >
            <Mic size={13} /> Start Notes
            <span className="text-[10px] text-saffron/80 ml-0.5 hidden sm:inline">· {lang}</span>
          </button>
        ) : (
          <button
            onClick={stop}
            data-testid="meeting-notes-stop"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-red-600/90 border border-red-500 text-white hover:bg-red-600"
            title="Stop recording"
          >
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
            <MicOff size={13} /> Stop
          </button>
        )}
        {(transcript || recording) && (
          <button
            onClick={() => setShowPanel((v) => !v)}
            data-testid="meeting-notes-panel-toggle"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white/5 border border-white/15 text-pearl/80 hover:border-saffron hover:text-saffron"
            title="Show transcript panel"
          >
            <FileText size={13} />
            {transcript.split(/\s+/).filter(Boolean).length} words
          </button>
        )}
      </div>

      {showPanel && (
        <div
          data-testid="meeting-notes-panel"
          className="absolute right-0 top-full mt-2 w-[min(420px,calc(100vw-2rem))] bg-[#0B1F4D] border border-white/15 rounded-lg shadow-2xl p-3 z-50 text-pearl"
        >
          <div className="flex items-center justify-between mb-2 gap-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-saffron inline-flex items-center gap-1.5">
              {recording && <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />}
              {recording ? "Recording…" : "Notes"}
            </div>
            <div className="flex items-center gap-1.5 flex-1 justify-end">
              <Globe size={11} className="text-pearl/50" />
              <select
                value={lang}
                onChange={(e) => changeLang(e.target.value)}
                data-testid="meeting-notes-language"
                className="bg-white/5 border border-white/15 text-pearl/85 text-[11px] rounded px-1.5 py-0.5 max-w-[160px] focus:outline-none focus:border-saffron"
                title="Recognition language"
              >
                {NOTES_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code} className="bg-[#0B1F4D]">
                    {l.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setShowPanel(false)}
                className="text-pearl/50 hover:text-pearl text-xs ml-1"
                aria-label="Close"
              >✕</button>
            </div>
          </div>
          <div
            className="bg-white/5 border border-white/10 rounded-md p-2 max-h-[180px] overflow-y-auto text-xs leading-relaxed text-pearl/85 whitespace-pre-wrap"
            data-testid="meeting-notes-transcript"
          >
            {transcript || (recording ? "" : <span className="italic text-pearl/40">Click "Start Notes". Your speech is transcribed locally in this browser.</span>)}
            {interim && <span className="text-pearl/40 italic">{interim}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <button
              onClick={summariseAndSave}
              disabled={busy || !transcript.trim() || transcript.trim().length < 20}
              data-testid="meeting-notes-summarise"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium bg-saffron/20 border border-saffron/40 text-saffron hover:bg-saffron/30 disabled:opacity-40"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {busy ? "Saving…" : savedId ? "Summarise again" : "Summarise & save"}
            </button>
            <button
              onClick={() => download("md")}
              disabled={!transcript.trim()}
              data-testid="meeting-notes-export-md"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium bg-white/5 border border-white/15 text-pearl/80 hover:border-saffron hover:text-saffron disabled:opacity-40"
            >
              <Download size={12} /> Markdown
            </button>
            <button
              onClick={() => download("txt")}
              disabled={!transcript.trim()}
              data-testid="meeting-notes-export-txt"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium bg-white/5 border border-white/15 text-pearl/80 hover:border-saffron hover:text-saffron disabled:opacity-40"
            >
              <Download size={12} /> Text
            </button>
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(transcript.trim());
                  toast.success("Transcript copied");
                } catch (e) { /* ignore */ }
              }}
              disabled={!transcript.trim()}
              data-testid="meeting-notes-copy"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium bg-white/5 border border-white/15 text-pearl/80 hover:border-saffron hover:text-saffron disabled:opacity-40"
            >
              Copy
            </button>
          </div>
          <div className="mt-2 text-[9px] text-pearl/45 leading-snug">
            🔒 Speech runs in your browser. Audio never leaves this device. Only the typed
            transcript is sent when you click "Summarise & save".
          </div>
        </div>
      )}
    </div>
  );
}
